/**
 * src/utils/promoHelper.js
 * Centralized logic for managing and validating promotion codes.
 */

const ACTIVE_PROMOS = [
  {
    code: "FTJ100",
    type: "FIXED_PRICE", 
    value: 0,
    description: "100% discount for your first month",
    expiry: "2026-12-31",
    maxSlots: 1000,
    currentUsage: 0,
    oncePerAccount: true // Ensures code can't be reused by the same user
  },
  {
    code: "FOUNDER0",
    type: "FIXED_PRICE", 
    value: 0,
    description: "100% discount for founding members",
    expiry: "2026-01-31",
    maxSlots: 50,
    currentUsage: 0,
    oncePerAccount: true
  },
  {
    code: "FOUNDING50",
    type: "PERCENTAGE",
    value: 50,
    description: "50% launch discount",
    expiry: "2026-12-31",
    maxSlots: 100,
    currentUsage: 0,
    oncePerAccount: true
  },
  {
    code: "OFFER200",
    type: "FLAT",
    value: 200,
    description: "Flat ₹200 discount",
    expiry: "2026-06-01",
    maxSlots: 500,
    currentUsage: 0,
    oncePerAccount: false // Example of a code that could be used multiple times
  }
];

/**
 * FUNCTION: validatePromo
 * @param {string} inputCode - The raw string entered by the user.
 * @param {Array} userHistory - Optional: Array of strings (codes) the user has already redeemed.
 */
export const validatePromo = (inputCode, userHistory = []) => {
  if (!inputCode) return { isValid: false, message: "Please enter a code." };

  const code = inputCode.toUpperCase().trim();
  const promo = ACTIVE_PROMOS.find((p) => p.code === code);

  // 1. Check if code exists
  if (!promo) {
    return { isValid: false, message: "Invalid promo code." };
  }

  // 2. Check if user has already used this code (New Logic)
  if (promo.oncePerAccount && userHistory.includes(code)) {
    return { isValid: false, message: "This code has already been used on this account." };
  }

  // 3. Check Expiry
  if (promo.expiry) {
    const expiryDate = new Date(promo.expiry);
    expiryDate.setHours(23, 59, 59);
    const today = new Date();
    
    if (today > expiryDate) {
      return { isValid: false, message: "This promo code has expired." };
    }
  }

  // 4. Check Slots
  if (promo.maxSlots && promo.currentUsage >= promo.maxSlots) {
    return { isValid: false, message: "Usage limit reached for this code." };
  }

  // If all checks pass
  return { 
    isValid: true, 
    code: promo.code,
    type: promo.type, 
    value: promo.value,
    message: `Promo applied: ${promo.description}` 
  };
};

export const calculateDiscountedPrice = (originalPrice, promo) => {
  if (!promo || !promo.isValid) return originalPrice;

  let finalPrice = originalPrice;

  switch (promo.type) {
    case "FIXED_PRICE":
      finalPrice = promo.value;
      break;

    case "PERCENTAGE":
      finalPrice = originalPrice - (originalPrice * (promo.value / 100));
      break;

    case "FLAT":
      finalPrice = originalPrice - promo.value;
      break;

    default:
      finalPrice = originalPrice;
  }

  return Math.max(0, finalPrice);
};